import { useUser, useAuth } from '@clerk/clerk-react';

/**
 * Custom hook to simplify Clerk authentication access
 * Provides user info and helper methods for getting tokens
 */
export function useClerkAuth() {
  const { user, isLoaded, isSignedIn } = useUser();
  const { getToken } = useAuth();

  /**
   * Get Clerk session JWT token
   * This token is used to authenticate with n8n webhooks
   */
  const getClerkJWT = async () => {
    try {
      return await getToken();
    } catch (error) {
      console.error('Failed to get Clerk JWT:', error);
      throw new Error('Authentication error. Please sign in again.');
    }
  };

  /**
   * Get Canva OAuth access token from Clerk External Accounts
   * Clerk automatically refreshes the token if it's expired
   */
  const getCanvaAccessToken = async () => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    const accounts = user?.externalAccounts || [];
    const canvaAccount = accounts.find(acc =>
      (acc.provider === 'canva' ||
       acc.provider === 'custom_canva' ||
       acc.provider === 'custom_canva2') &&
      acc.verification?.status === 'verified'
    );

    if (!canvaAccount) {
      throw new Error('Canva account not linked. Please reconnect your Canva account.');
    }

    return canvaAccount.accessToken;
  };

  /**
   * Check if user has connected their Canva account.
   * DEV-only: window.__turboSyncTestCanvaConnected overrides the real check
   * so Cypress tests can exercise the designs UI without a real Canva OAuth
   * token on the test account. Set via cy.visit onBeforeLoad; never in prod.
   */
  const hasCanvaConnected = () => {
    if (import.meta.env.DEV && window.__turboSyncTestCanvaConnected !== undefined) {
      return !!window.__turboSyncTestCanvaConnected;
    }
    if (!isLoaded || !user) return false;
    return user.externalAccounts.some(acc =>
      (acc.provider === 'canva' ||
       acc.provider === 'custom_canva' ||
       acc.provider === 'custom_canva2') &&
      acc.verification?.status === 'verified'
    );
  };

  return {
    // User info
    user,
    isLoaded,
    isSignedIn,
    clerkUserId: user?.id,
    email: user?.primaryEmailAddress?.emailAddress,
    firstName: user?.firstName,
    lastName: user?.lastName,
    fullName: user?.fullName,
    imageUrl: user?.imageUrl,
    
    // Auth methods
    getClerkJWT,
    getCanvaAccessToken,
    hasCanvaConnected,
  };
}



